import styles from "./homedashboard.module.css";
import moduleStyles from "components/module/styles.module.css";

import React, { useRef, useState, useEffect, useMemo, useImperativeHandle } from 'react';
import Spacer from "components/layout/Spacer";
import MetaIdbRepository from "repositories/idb/MetaIdbRepository";
import { ApplicationRightService, IApplicationRightService } from "services/ApplicationRightService";
import { ModuleProps } from 'components/props/ModuleProps';
import { isArrayEmpty } from 'components/helpers/ObjectHelpers';
import { ModuleService } from 'services/ModuleService';
import { ModuleMyPilotAssignments, ModuleMyPilotAssignmentsHandle } from "./modules/ModuleMyPilotAssignments";
import { ModuleIncident, ModuleIncidentHandle } from "./modules/ModuleIncident";
import { useNavigate } from "react-router";
import { IconPicker } from "components/layout/icons/IconPicker";
import { DashboardDefaultItem } from "components/dashboard/DashboardDefaultItem";

interface HomeDashboardProps {
    ref: any;
    applicationRights: any[];
}

export const HomeDashboard = ({
    ref,
    applicationRights,
}:HomeDashboardProps) => {

    const [refresh, setRefresh] = useState(false);

    useImperativeHandle(ref, () => ({
        onRefresh() {
            pilotAssignmentsRef.current?.onRefresh();
            incidentsRef.current?.onRefresh();

            setRefresh(true);
            setTimeout(() => setRefresh(false), 1000);
        }
    }));
    
    const pilotAssignmentsRef = useRef<ModuleMyPilotAssignmentsHandle>(null);
    const incidentsRef = useRef<ModuleIncidentHandle>(null);
    
    const [modules, setModules] = useState<ModuleProps[]>([]);
    const applicationRightService: IApplicationRightService = useMemo(() => new ApplicationRightService(applicationRights),[applicationRights]);

    const navigate = useNavigate();

    const shouldAdd = (module: ModuleProps) => {
        let shouldAdd = true;
        if (!navigator.onLine && module.onlineOnly) {
            shouldAdd = false;
        }
        return shouldAdd;
    }

    const initialize = async () => {
        const idbModules = await MetaIdbRepository.getModulesAsync();
        const result: ModuleProps[] = [];

        if(idbModules) {
            const idbModuleValues = idbModules.value || [];
            for (const idbModule of idbModuleValues) {
                const module = ModuleService.getModuleById(idbModule.id);
                if (applicationRightService.canAccess(module.applicationRights)) {
                    if (shouldAdd(idbModule)) {
                        result.push(idbModule);
                    }
                }
            }
        }

        setModules(result.sort((a, b) => a.sequenceNo - b.sequenceNo));
    }

    useEffect(() => {

        initialize();

        window.addEventListener("offline", initialize);
        window.addEventListener("online", initialize);

        return (() => {
            window.removeEventListener("offline", initialize);
            window.removeEventListener("online", initialize);
        })

    }, []);

    const onModuleClick = (module: ModuleProps) => {
        navigate(module.url);
    }

    const onDefault = async () => {
        await ModuleService.setDefaultDashboard(applicationRights);
        initialize();
    }

    const renderModuleDashboard = (module: ModuleProps, index: number) => {
        // Get the full module definition from ModuleService
        // Check if module has a dashboard component defined
         const fullModule = ModuleService.getModuleById(module.id);
        // Check if module has a dashboard component defined
        if (fullModule.dashboard) {
            const DashboardComponent = fullModule.dashboard;
            return (
                <DashboardComponent
                    key={index}
                    refresh={refresh}
                />
            );
        }

        // Fallback to legacy switch statement for modules not yet migrated
        switch (module.id) {
            case "MY_PILOT_ASSIGNMENTS":
                return (
                    <ModuleMyPilotAssignments
                        key={index}
                        module={module}
                        onClick={onModuleClick}
                        ref={pilotAssignmentsRef} />);

            case "INCIDENTS":
                return (
                    <ModuleIncident
                        key={index}
                        module={module}
                        onClick={onModuleClick}
                        ref={incidentsRef}
                    />);

            default:
                return (
                    <DashboardDefaultItem
                        key={index}
                        module={module}
                        onClick={onModuleClick}/>
                );
        }
    }

    return (
        <>
            <div className={styles.doubleGrid}>
                {modules.map((module, index) => renderModuleDashboard(module, index))}
            </div>

            {
                isArrayEmpty(modules) &&
                <>
                    <div className={moduleStyles.bar} onClick={() => navigate('/settings/PREFERENCES')}>
                        <div className={moduleStyles.barContent}>
                            <div role="heading">Velg hvilke kort fra menyen som skal vises</div>
                            <div><IconPicker icon="gear" size={24} /></div>
                        </div>
                        <Spacer height={10} />
                        <div>
                            Nå kan du selv velge hvilke valg fra hovedmenyen som skal vises på Hjem-siden som kort og i hvilken rekkefølge.
                        </div>
                        <Spacer height={10} />
                        <div>
                            Dette gjør du enten ved å klikke på dette kortet, eller navigere til Preferanser fra hovedmenyen.
                        </div>
                    </div>
                    <Spacer height={10} />
                    <div className={moduleStyles.bar} onClick={onDefault}>
                        <div className={moduleStyles.barContent}>
                            <div role="heading">Standard forside</div>
                            <div><IconPicker icon="squares-four" size={24} /></div>
                        </div>
                        <Spacer height={10} />
                        <div>
                            Klikk her hvis du ønsker å bruke standard forside.
                        </div>
                    </div>
                </>
            }

            <Spacer height={50}/>
            
        </>
    )
}
